/**
 * BrandLogo Component
 * Renders the brand logo image, falling back to styled text
 * (appName large + appDescription small) when no valid logo is available.
 */

import React, { useState } from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';
import branding from '../../config/branding';

interface BrandLogoProps {
  /** Which logo variant to use */
  variant: 'light' | 'dark';
  /** Size preset — 'sm' for compact spaces like AppBar, 'md' default */
  size?: 'sm' | 'md';
  /** Alt text override */
  alt?: string;
  /** Sx props applied to the outer container */
  sx?: SxProps<Theme>;
}

const logoMap = {
  light: branding.logoLight,
  dark: branding.logoDark,
} as const;

const sizeMap = {
  sm: { title: '1.05rem', subtitle: '0.5rem' },
  md: { title: '2rem', subtitle: '0.75rem' },
} as const;

const BrandLogo: React.FC<BrandLogoProps> = ({ variant, size = 'md', alt, sx }) => {
  const logoSrc = logoMap[variant];
  const [imgFailed, setImgFailed] = useState(false);

  const isLight = variant === 'light';
  const textColor = isLight ? '#fff' : 'text.primary';
  const sizes = sizeMap[size];

  if (!logoSrc || imgFailed) {
    return (
      <Box sx={{ textAlign: 'center', whiteSpace: 'nowrap', ...sx as Record<string, unknown> }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: sizes.title,
            lineHeight: 1.1,
            letterSpacing: '0.08em',
            color: textColor,
          }}
        >
          {branding.appName}
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: sizes.subtitle,
            letterSpacing: '0.12em',
            color: textColor,
            opacity: 0.85,
          }}
        >
          {branding.appDescription}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={logoSrc}
      alt={alt ?? branding.appName}
      onError={() => setImgFailed(true)}
      sx={sx}
    />
  );
};

export default BrandLogo;
